'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, INLINES, Node } from '@contentful/rich-text-types';
import renderEmbeddedEntry from '@/components/nodes/EmbeddedEntry';
import renderEntryHyperlink from '@/components/nodes/EntryHyperlink';
import renderEmbeddedAsset from '@/components/nodes/EmbeddedAsset';
import Link from '@/components/shared/Link/Link';
import type { DocumentWithEmbeddedEntries } from '@/libs/contentful/types';
import { useDisclosureNumberingMap } from '@/contexts/DisclosureProvider';
import { extractTextFromNode, getIdFromText, getLetterLabel } from '@/utils/formatters';
import { AgreementTable } from './AgreementTable';
import { getTocAnchor, TOC_HEADING_RE, TOC_MARKER_RE } from './consts';

interface AgreementRichTextProps {
  body: DocumentWithEmbeddedEntries;
  sectionTitle?: string;
}

const AgreementRichText = ({ body, sectionTitle }: AgreementRichTextProps) => {
  const disclosureNumberingMap = useDisclosureNumberingMap();
  const sectionAnchor = sectionTitle ? getIdFromText(sectionTitle) : undefined;
  const tocAnchorCounts = new Map<string, number>();
  let tocHeadingIndex = 0;

  const options: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactNode) => {
        const text = extractTextFromNode(node);
        const match = text.match(TOC_HEADING_RE);
        if (match) {
          const headingText = match[1];
          if (!sectionAnchor) {
            return <p className="my-4 last:mb-0">{headingText}</p>;
          }

          const headingLabel = `${getLetterLabel(tocHeadingIndex)}. ${headingText}`;
          const anchor = getTocAnchor(sectionAnchor, headingText, tocAnchorCounts);
          tocHeadingIndex += 1;

          return (
            <p id={anchor} className="font-sans font-semibold my-4 last:mb-0">
              {headingLabel}
            </p>
          );
        }
        return <p className="my-4 last:mb-0">{children}</p>;
      },
      [BLOCKS.HEADING_2]: (_node: Node, children: React.ReactNode) => (
        <h2 className="font-sans font-semibold text-heading-md my-8">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (_node: Node, children: React.ReactNode) => (
        <h3 className="font-sans font-semibold text-heading-sm my-4">{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (_node: Node, children: React.ReactNode) => (
        <h4 className="font-sans font-semibold my-4">{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (_node: Node, children: React.ReactNode) => (
        <h5 className="font-sans font-semibold my-4">{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (_node: Node, children: React.ReactNode) => (
        <h6 className="font-sans font-semibold my-4">{children}</h6>
      ),
      [BLOCKS.UL_LIST]: (node: Node, children: React.ReactNode) => {
        const listStyleType = typeof node.data?.listStyleType === 'string' ? node.data.listStyleType : undefined;
        return (
          <ul className="list-disc pl-5 my-4 [&_ul]:pl-5" style={listStyleType ? { listStyleType } : undefined}>
            {children}
          </ul>
        );
      },
      [BLOCKS.OL_LIST]: (node: Node, children: React.ReactNode) => {
        const listStyleType = typeof node.data?.listStyleType === 'string' ? node.data.listStyleType : undefined;
        return (
          <ol className="list-decimal pl-5 my-4 [&_ol]:pl-5" style={listStyleType ? { listStyleType } : undefined}>
            {children}
          </ol>
        );
      },
      [BLOCKS.LIST_ITEM]: (_node: Node, children: React.ReactNode) => <li className="my-1">{children}</li>,
      [BLOCKS.TABLE]: (node: Node) => <AgreementTable node={node as Block} renderOptions={options} />,
      [BLOCKS.HR]: () => <hr className="my-4 border-content-primary" />,
      [BLOCKS.EMBEDDED_ASSET]: (node: Node, children: React.ReactNode) => {
        return <div className="my-4">{renderEmbeddedAsset(node, children, body)}</div>;
      },
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => (
        <Link href={node.data.uri} className="inline underline">
          {children}
        </Link>
      ),
      [INLINES.ENTRY_HYPERLINK]: (node: Node, children: React.ReactNode) => {
        return renderEntryHyperlink(node, children, body);
      },
      [INLINES.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) => {
        return renderEmbeddedEntry(node, children, body, disclosureNumberingMap);
      },
    },
    renderText: (text: string) => {
      // Strip `[toc]` and `[/toc]` markers so they don't leak into the rendered copy.
      const cleaned = text.replace(TOC_MARKER_RE, '');
      return cleaned.split('\n').reduce<React.ReactNode[]>((acc, segment, i) => {
        if (i > 0) acc.push(<br key={`br-${i}`} />);
        acc.push(segment);
        return acc;
      }, []);
    },
  };

  if (!body.json) return null;

  return documentToReactComponents(body.json, options);
};

export { AgreementRichText };
